import type React from 'react';

import {
  CallSplitRounded,
  CallReceivedRounded,
  DashboardRounded,
  ExtensionRounded,
  GraphicEqRounded,
  HomeRounded,
  LabelRounded,
  PeopleAltRounded,
  PhoneForwardedRounded,
  PhoneLockedRounded,
  WorkspacesRounded,
  PublicRounded,
  SipRounded,
  AssessmentRounded,
  SimCardRounded,
  MultipleStopRounded,
  ShapeLineRounded,
  RouteRounded,
  ManageAccountsRounded,
  CalendarTodayRounded,
  BadgeRounded,
  QueueRounded,
  QueueMusicRounded,
  DynamicFeedRounded,
  ContactsRounded,
  RecentActorsRounded,
  SecurityRounded,
  PhoneRounded,
  VideoLibraryRounded,
  AudiotrackRounded,
  AppsRounded,
  FiberManualRecordRounded,
  HistoryRounded,
  SettingsInputAntennaRounded,
  HubRounded,
  DevicesRounded,
  IntegrationInstructionsRounded,
  EnhancedEncryptionRounded,
  TuneRounded,
  StorageRounded,
  VpnKeyRounded,
  SyncRounded,
} from '@mui/icons-material';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  OrdMenu,
  OrdMultiLevelMenu,
  OrdMenuGroupDivider,
  CustomMenuItem,
  ResourceMenuItem,
} from '@ragu-components/ord-components';
import { useOrdIdentity } from '@ragu-components/ord-providers';
import { useSidebarState, useTranslate } from 'react-admin';

import { AuthProviderRounded, RoleRounded } from 'src/components/icons';

import { Routes } from './Routes';

export const Menu = (): React.ReactElement => {
  const theme = useTheme();
  const isExtended = useMediaQuery(theme.breakpoints.up('sm'));
  const isOpen = useSidebarState()[0];
  const translate = useTranslate();
  const { identity } = useOrdIdentity();

  // List of routes in each group used to correctly display OrdMenuGroupDividers based on permissions
  const systemRoutes = ['domains', 'lcrs', 'sip_profiles', 'carriers', 'inbound_e164s', 'carrier_mappings'];
  const adminRoutes = [
    'assigned_carriers',
    'assigned_inbound_e164s',
    'assigned_lcrs',
    'assigned_sip_profiles',
    'auth_providers',
    'oauth_applications',
    'ap_configurations',
    'ap_groups',
    'ap_templates',
    'sip_devices',
    'calendar_events',
    'calendars',
    'callflows',
    'extensiosn',
    'groups',
    'labels',
    'user_mappings',
    'mobility_configurations',
    'queues',
    'call_recording_confs',
    'service_classes',
    'encryption_configurations',
    'storage_configurations',
    'scim_provisionings',
    'sip_identities',
    'soundfiles',
    'users',
  ];
  const reportingRoutes = ['queue_call_sessions', 'call_sessions', 'call_recordings'];

  return (
    <OrdMenu>
      <CustomMenuItem
        route={Routes.home}
        name='home'
        alternativeRoute='/'
        icon={<HomeRounded />}
        activeIcon={undefined}
      />
      <CustomMenuItem
        route={Routes.console}
        name='console'
        icon={<DashboardRounded />}
        target='_blank'
        rel='noreferrer'
      />
      <ResourceMenuItem
        route='me/calendar_events'
        primaryText={translate('ord.menu.myCalendarsLabel')}
        icon={<CalendarTodayRounded />}
        activeIcon={undefined}
      />
      <OrdMultiLevelMenu
        label={translate('ord.menu.contactsLabel')}
        icon={<ContactsRounded />}
        parentSize={isOpen ? 'expanded' : 'compressed'}
      >
        <ResourceMenuItem route='contacts' icon={<RecentActorsRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='distribution_lists' icon={<DynamicFeedRounded />} activeIcon={undefined} />
      </OrdMultiLevelMenu>
      <ResourceMenuItem
        route='me/mobility_configurations'
        primaryText={translate('ord.menu.mobilityLabel')}
        icon={<PhoneForwardedRounded />}
        activeIcon={undefined}
      />
      {identity?.can_access_own_recordings === true ? (
        <OrdMultiLevelMenu
          label={translate('ord.menu.callsLabel', { _: 'Calls' })}
          icon={<PhoneRounded />}
          parentSize={isOpen ? 'expanded' : 'compressed'}
        >
          <ResourceMenuItem route='me/call_history' icon={<HistoryRounded />} activeIcon={undefined} />
          <ResourceMenuItem route='me/call_recordings' icon={<GraphicEqRounded />} activeIcon={undefined} />
        </OrdMultiLevelMenu>
      ) : (
        <ResourceMenuItem route='me/call_history' icon={<HistoryRounded />} activeIcon={undefined} />
      )}
      <OrdMenuGroupDivider label={isExtended && isOpen ? 'ord.menu.adminLabel' : undefined} routes={adminRoutes} />
      <OrdMultiLevelMenu
        label={translate('ord.menu.usersLabel')}
        icon={<ManageAccountsRounded />}
        parentSize={isOpen ? 'expanded' : 'compressed'}
      >
        <ResourceMenuItem route='users' icon={<PeopleAltRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='groups' icon={<WorkspacesRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='extensions' icon={<ExtensionRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='sip_identities' icon={<PhoneLockedRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='mobility_configurations' icon={<PhoneForwardedRounded />} activeIcon={undefined} />
      </OrdMultiLevelMenu>
      <OrdMultiLevelMenu
        label={translate('ord.menu.labelsLabel')}
        icon={<LabelRounded />}
        parentSize={isOpen ? 'expanded' : 'compressed'}
      >
        <ResourceMenuItem route='labels' icon={<LabelRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='presence/users' icon={<PeopleAltRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='queue_measurements' icon={<QueueRounded />} activeIcon={undefined} />
      </OrdMultiLevelMenu>
      <ResourceMenuItem
        route='calendar_events'
        primaryText={translate('ord.menu.calendarsLabel')}
        icon={<CalendarTodayRounded />}
        activeIcon={undefined}
      />
      <OrdMultiLevelMenu
        label={translate('ord.menu.callManagementLabel')}
        icon={<CallSplitRounded />}
        parentSize={isOpen ? 'expanded' : 'compressed'}
      >
        <ResourceMenuItem route='callflows' icon={<CallSplitRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='queues' icon={<QueueRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='call_recording_confs' icon={<FiberManualRecordRounded />} activeIcon={undefined} />
      </OrdMultiLevelMenu>
      <OrdMultiLevelMenu
        label={translate('ord.menu.routingAssignedLabel')}
        icon={<RouteRounded />}
        parentSize={isOpen ? 'expanded' : 'compressed'}
      >
        <ResourceMenuItem route='assigned_lcrs' icon={<MultipleStopRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='assigned_sip_profiles' icon={<SipRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='assigned_carriers' icon={<SimCardRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='assigned_inbound_e164s' icon={<CallReceivedRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='user_mappings' icon={<ShapeLineRounded />} activeIcon={undefined} />
      </OrdMultiLevelMenu>
      <OrdMultiLevelMenu
        label={translate('ord.menu.autoprovisioningLabel', { _: 'Autoprovisioning' })}
        icon={<SettingsInputAntennaRounded />}
        parentSize={isOpen ? 'expanded' : 'compressed'}
      >
        <ResourceMenuItem route='ap_configurations' icon={<SettingsInputAntennaRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='ap_groups' icon={<HubRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='sip_devices' icon={<DevicesRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='ap_templates' icon={<IntegrationInstructionsRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='ap_authentication' icon={<VpnKeyRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='ap_settings' icon={<TuneRounded />} activeIcon={undefined} />
      </OrdMultiLevelMenu>
      <OrdMultiLevelMenu
        label={translate('ord.menu.securityLabel')}
        icon={<SecurityRounded />}
        parentSize={isOpen ? 'expanded' : 'compressed'}
      >
        <ResourceMenuItem route='service_classes' icon={<BadgeRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='auth_providers' icon={<AuthProviderRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='oauth_applications' icon={<AppsRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='roles' icon={<RoleRounded />} activeIcon={undefined} />
        <ResourceMenuItem
          route='encryption_configurations'
          icon={<EnhancedEncryptionRounded />}
          activeIcon={undefined}
        />
        <ResourceMenuItem route='storage_configurations' icon={<StorageRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='scim_provisionings' icon={<SyncRounded />} activeIcon={undefined} />
      </OrdMultiLevelMenu>
      <OrdMultiLevelMenu
        label={translate('ord.menu.mediaLabel')}
        icon={<VideoLibraryRounded />}
        parentSize={isOpen ? 'expanded' : 'compressed'}
      >
        <ResourceMenuItem route='soundfiles' icon={<AudiotrackRounded />} activeIcon={undefined} />
      </OrdMultiLevelMenu>
      <OrdMenuGroupDivider label={isExtended && isOpen ? 'ord.menu.reports' : undefined} routes={reportingRoutes} />
      <OrdMultiLevelMenu
        label={translate('resources.call_reports.name')}
        icon={<AssessmentRounded />}
        parentSize={isOpen ? 'expanded' : 'compressed'}
      >
        <ResourceMenuItem route='call_sessions' icon={<PhoneRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='queue_call_sessions' icon={<QueueMusicRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='call_recordings' icon={<GraphicEqRounded />} activeIcon={undefined} />
      </OrdMultiLevelMenu>

      <OrdMenuGroupDivider label={isExtended && isOpen ? 'ord.menu.systemLabel' : undefined} routes={systemRoutes} />
      <ResourceMenuItem route='domains' icon={<PublicRounded />} activeIcon={undefined} />
      <OrdMultiLevelMenu
        label={translate('ord.menu.routingLabel')}
        icon={<RouteRounded />}
        parentSize={isOpen ? 'expanded' : 'compressed'}
      >
        <ResourceMenuItem route='lcrs' icon={<MultipleStopRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='sip_profiles' icon={<SipRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='carriers' icon={<SimCardRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='inbound_e164s' icon={<CallReceivedRounded />} activeIcon={undefined} />
        <ResourceMenuItem route='carrier_mappings' icon={<ShapeLineRounded />} activeIcon={undefined} />
      </OrdMultiLevelMenu>
    </OrdMenu>
  );
};
